import React, { useEffect, useMemo, useState } from 'react';
import { Button, Checkbox, CircularProgress, Dialog, DialogContent, DialogTitle } from '@material-ui/core';
import { Autocomplete, TextField } from '@mui/material';
import { addSongsToPlaylist } from '../../api/playlist';
import { fetcher } from '../../utils/axios';
import { MEDIA_URL } from '../../utils/consts';
import { colorIsDarkSimple } from '../../generalConstant';
import PlaylistActionIcon from './PlaylistActionIcon';
import RangeSelectionButton from '../RangeSelectionButton';
import useRangeSelection, { updateSelectedIds } from '../../utils/useRangeSelection';
import './PlaylistManagement.scss';

const PAGE_SIZE = 40;

export default function PlaylistCopySongsDialog({ playlist, open, onClose, onSuccess, initialSelectedIds }) {
  const [selectedIds, setSelectedIds] = useState([]);
  const { choose: chooseRange, toggleRangeMode, resetRange, isRangeMode } = useRangeSelection();
  const [playlists, setPlaylists] = useState([]);
  const [loadingPlaylists, setLoadingPlaylists] = useState(false);
  const [targetId, setTargetId] = useState('');
  const [playlistSearch, setPlaylistSearch] = useState('');
  const [playlistType, setPlaylistType] = useState('all');
  const [selectedTagIds, setSelectedTagIds] = useState([]);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [reloadCount, setReloadCount] = useState(0);
  const [busy, setBusy] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [error, setError] = useState('');
  const [resultMessage, setResultMessage] = useState('');
  const [previewSongId, setPreviewSongId] = useState(null);
  const [previewError, setPreviewError] = useState('');

  useEffect(() => {
    if (!open) return;
    const availableIds = (playlist?.songs || []).map((song) => song.id);
    setSelectedIds(initialSelectedIds ? availableIds.filter((id) => initialSelectedIds.includes(id)) : availableIds);
    resetRange('copy-songs');
    setTargetId('');
    setPlaylistSearch('');
    setPlaylistType('all');
    setSelectedTagIds([]);
    setVisibleCount(PAGE_SIZE);
    setError('');
    setLoadError('');
    setResultMessage('');
    setPreviewSongId(null);
    setPreviewError('');
  }, [open, playlist?.id, playlist?.songs, initialSelectedIds, resetRange]);

  useEffect(() => {
    if (!open) return;
    let active = true;
    setLoadError('');
    setPlaylists([]);
    setLoadingPlaylists(true);
    fetcher('/playlists/')
      .then((items) => { if (active) setPlaylists(Array.isArray(items) ? items : []); })
      .catch(() => { if (active) setLoadError('Could not load destination playlists.'); })
      .finally(() => { if (active) setLoadingPlaylists(false); });
    return () => { active = false; };
  }, [open, playlist?.id, reloadCount]);

  const sourceSongs = playlist?.songs || [];
  const previewSong = sourceSongs.find((song) => song.id === previewSongId);
  const songName = (song) => song.name || song.title || song.fileName || 'Unnamed song';
  const targets = useMemo(() => playlists
    .filter((item) => item.id !== playlist?.id && (item.type === 3 || item.type === 4))
    .sort((left, right) => (left.name || '').localeCompare(right.name || '')), [playlists, playlist?.id]);
  const tagOptions = useMemo(() => {
    const tags = new Map();
    targets.forEach((item) => (item.tags || []).forEach((tag) => {
      const id = tag.tagId || tag.id;
      if (id && !tags.has(id)) tags.set(id, { ...tag, id });
    }));
    return [...tags.values()].sort((left, right) => (left.name || '').localeCompare(right.name || ''));
  }, [targets]);
  const filteredTargets = useMemo(() => targets.filter((item) =>
    (playlistType === 'all' || item.type === (playlistType === 'royalty' ? 3 : 4)) &&
    (selectedTagIds.length === 0 || (item.tags || []).some((tag) => selectedTagIds.includes(tag.tagId || tag.id))) &&
    (item.name || '').toLowerCase().includes(playlistSearch.trim().toLowerCase())),
  [targets, playlistType, selectedTagIds, playlistSearch]);
  const selectedTarget = targets.find((item) => item.id === targetId);
  const alreadyInTarget = selectedTarget
    ? new Set((selectedTarget.songs || []).map((song) => song.id))
    : new Set();
  const newSongCount = selectedIds.filter((id) => !alreadyInTarget.has(id)).length;
  const alreadyCopiedCount = selectedIds.length - newSongCount;

  const toggleSong = (song, event) => {
    setResultMessage('');
    const selection = chooseRange('copy-songs', sourceSongs, song, event.target.checked, !!event.nativeEvent?.shiftKey);
    setSelectedIds((current) => updateSelectedIds(current, selection));
  };

  const closeDialog = () => {
    setPreviewSongId(null);
    setPreviewError('');
    onClose();
  };

  const copySongs = async () => {
    if (!targetId || !selectedIds.length || busy) return;
    setBusy(true);
    setError('');
    setResultMessage('');
    try {
      const orderedIds = sourceSongs.map((song) => song.id).filter((id) => selectedIds.includes(id));
      const result = await addSongsToPlaylist(targetId, orderedIds);
      setResultMessage(result.addedSongIds.length
        ? `${result.addedSongIds.length} song${result.addedSongIds.length === 1 ? '' : 's'} added to ${selectedTarget.name}. Songs already there were skipped.`
        : `All selected songs are already in ${selectedTarget.name}. Nothing was changed.`);
      if (result.addedSongIds.length) {
        const added = new Set(result.addedSongIds);
        setPlaylists((current) => current.map((item) => item.id === targetId
          ? { ...item, songs: [...(item.songs || []), ...sourceSongs.filter((song) => added.has(song.id))] }
          : item));
      }
      if (onSuccess) onSuccess();
    } catch (err) {
      setError('Could not copy the songs. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return <Dialog className="playlist-management-dialog playlist-copy-dialog" open={open} onClose={busy ? undefined : closeDialog} maxWidth={false}>
    <DialogTitle>Copy songs from {playlist?.name}</DialogTitle>
    <DialogContent>
      <p className="playlist-management-description">Choose a destination, then select all or individual songs to copy. Songs already in the destination are skipped.</p>
      <strong className="playlist-management-label">Destination playlist</strong>
      <div className="playlist-copy-filters">
        <input
          className="playlist-management-search"
          type="search"
          placeholder="Search playlists"
          aria-label="Search destination playlists"
          value={playlistSearch}
          onChange={(event) => { setPlaylistSearch(event.target.value); setVisibleCount(PAGE_SIZE); }}
        />
        <Autocomplete
          multiple
          size="small"
          limitTags={2}
          options={tagOptions}
          value={tagOptions.filter((tag) => selectedTagIds.includes(tag.id))}
          getOptionLabel={(tag) => tag.name || ''}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          filterSelectedOptions
          onChange={(_, tags) => { setSelectedTagIds(tags.map((tag) => tag.id)); setVisibleCount(PAGE_SIZE); }}
          renderInput={(params) => <TextField {...params} placeholder="Filter by tags" aria-label="Filter destination playlists by tags" />}
        />
      </div>
      <div className="playlist-copy-types" role="group" aria-label="Filter destination playlists by type">
        {[['all', 'All'], ['royalty', 'Royalty'], ['non-royalty', 'Non-Royalty']].map(([value, label]) => <button
          key={value}
          type="button"
          className={playlistType === value ? 'active' : ''}
          aria-pressed={playlistType === value}
          onClick={() => { setPlaylistType(value); setVisibleCount(PAGE_SIZE); }}
        >{label}</button>)}
      </div>
      <div className="playlist-copy-summary">
        <span>{loadingPlaylists ? 'Loading playlists...' : `Showing ${filteredTargets.length} of ${targets.length} playlists`}</span>
        {selectedTarget && <strong>Destination: {selectedTarget.name}</strong>}
      </div>
      {selectedTarget && selectedIds.length > 0 && !resultMessage && <p className={newSongCount ? 'playlist-copy-available' : 'playlist-copy-already'} role="status">
        {newSongCount === 0
          ? `All ${selectedIds.length} selected song${selectedIds.length === 1 ? ' is' : 's are'} already in ${selectedTarget.name}. Copying again would not create duplicates.`
          : `${newSongCount} song${newSongCount === 1 ? '' : 's'} will be added to ${selectedTarget.name}.${alreadyCopiedCount ? ` ${alreadyCopiedCount} already there will be skipped.` : ''}`}
      </p>}
      {selectedTarget && selectedIds.length === 0 && <p className="playlist-copy-already" role="status">Select at least one song to copy.</p>}
      {resultMessage && <p className="playlist-management-success" role="status">{resultMessage}</p>}
      {loadError && <p className="playlist-management-error" role="alert">{loadError} <Button size="small" onClick={() => setReloadCount((count) => count + 1)}>Try again</Button></p>}
      <div className="playlist-copy-table" role="group" aria-label="Destination playlists">
        <div className="playlist-copy-table-header"><span>Playlist</span><span>Type</span><span>Tags</span><span>Songs</span><span>Action</span></div>
        {loadingPlaylists ? <div className="playlist-management-loading"><CircularProgress size={24} /></div> : <>
          {filteredTargets.slice(0, visibleCount).map((item) => <div className={`playlist-copy-table-row${targetId === item.id ? ' selected' : ''}`} key={item.id}>
            <span className="playlist-copy-name"><span className="playlist-copy-avatar" aria-hidden="true">{item.name?.charAt(0)?.toUpperCase() || 'P'}</span><strong>{item.name || 'Unnamed playlist'}</strong></span>
            <span>{item.type === 3 ? 'Royalty' : 'Non-Royalty'}</span>
            <span className="playlist-copy-tags">{item.tags?.length ? item.tags.map((tag) => <span key={tag.tagId || tag.id} style={{ backgroundColor: tag.color || '#e7ddff', color: tag.color && colorIsDarkSimple(tag.color) ? '#fff' : '#111' }}>{tag.name}</span>) : '—'}</span>
            <span>{item.songs?.length || 0}</span>
            <Button color="primary" variant={targetId === item.id ? 'contained' : 'outlined'} disabled={busy} onClick={() => { setTargetId(item.id); setResultMessage(''); }} aria-label={`Select ${item.name || 'playlist'} as destination`}>{targetId === item.id ? 'Selected' : 'Select'}</Button>
          </div>)}
          {!filteredTargets.length && <p className="playlist-copy-empty">{targets.length ? 'No playlists match your filters.' : 'No destination playlists are available.'}</p>}
          {visibleCount < filteredTargets.length && <div className="playlist-copy-more"><Button onClick={() => setVisibleCount((count) => count + PAGE_SIZE)}>Show more playlists</Button></div>}
        </>}
      </div>
      <div className="playlist-management-select-all">
        <strong>Songs ({selectedIds.length} selected)</strong>
        <Button onClick={() => { setResultMessage(''); resetRange('copy-songs'); setSelectedIds(selectedIds.length === sourceSongs.length ? [] : sourceSongs.map((song) => song.id)); }}>
          {selectedIds.length === sourceSongs.length ? 'Clear all' : 'Select all'}
        </Button>
        <RangeSelectionButton active={isRangeMode('copy-songs')} onClick={() => toggleRangeMode('copy-songs')} />
      </div>
      {previewSong && <div className="playlist-management-preview">
        <strong>Listening: {songName(previewSong)}</strong>
        <audio
          key={previewSong.id}
          src={`${MEDIA_URL}songs/${previewSong.id}.mp3`}
          controls
          autoPlay
          onError={() => setPreviewError('Could not play this song. Please try another song.')}
          onEnded={() => setPreviewSongId(null)}
        />
      </div>}
      {previewError && <p className="playlist-management-error" role="alert">{previewError}</p>}
      <div className="playlist-management-list">
        {sourceSongs.map((song) => <div className="playlist-management-row" key={song.id}>
          <label className="playlist-management-song-label">
            <Checkbox checked={selectedIds.includes(song.id)} onChange={(event) => toggleSong(song, event)} inputProps={{ 'aria-label': `Select ${songName(song)}` }} />
            <span>{songName(song)}</span>
          </label>
          {targetId && alreadyInTarget.has(song.id) && <small>Already in destination</small>}
          <PlaylistActionIcon kind="listen" label={`${previewSongId === song.id ? 'Stop' : 'Listen to'} ${songName(song)}`} active={previewSongId === song.id} onClick={() => {
            setPreviewError('');
            setPreviewSongId((current) => current === song.id ? null : song.id);
          }} />
        </div>)}
        {sourceSongs.length === 0 && <p>This playlist has no songs to copy.</p>}
      </div>
      {error && <p className="playlist-management-error" role="alert">{error}</p>}
      <div className="playlist-management-footer">
        <Button onClick={closeDialog} disabled={busy}>Close</Button>
        <Button color="primary" variant="contained" onClick={copySongs} disabled={busy || !targetId || !selectedIds.length || !newSongCount}>
          {busy ? <CircularProgress size={18} /> : selectedTarget && selectedIds.length && !newSongCount ? 'Already copied' : 'Copy songs'}
        </Button>
      </div>
    </DialogContent>
  </Dialog>;
}
